import { ImagePlus, Info, Send, Sparkles } from "lucide-react";
import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { insertHtmlAtSelection } from "@/features/study-group-events/components/utils/contentEditableHelpers";
import { sanitizeHtml } from "@/lib/utils/htmlSanitizer";
import { TIME_UNTIL_EVENT_TOKEN } from "@/lib/utils/reminderEmailBody";

interface ReminderEditorProps {
  subjectFieldId: string;
  bodyFieldId: string;
  subject: string;
  body: string;
  templateId?: string | number;
  isSubmitting: boolean;
  hasChanges: boolean;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onOpenEventInfo: () => void;
  onOpenSendNowDialog: () => void;
  onSendTest: () => void;
  onSave: () => void;
  subjectInputRef: React.RefObject<HTMLInputElement | null>;
  bodyTextareaRef: React.RefObject<HTMLDivElement | null>;
  showImageButton?: boolean;
  showTimeUntilButton?: boolean;
  showSendNowButton?: boolean;
  disableSendNow?: boolean;
  sendNowDisabledReason?: string | null;
  isActionsDisabled?: boolean;
  onInsertImagePlaceholder: () => void;
  onInsertTimeUntilEventPlaceholder: () => void;
  imagePlaceholderHtml: string;
  timeUntilEventPlaceholderHtml: string;
  subjectLimit?: number;
  bodyLimit?: number;
}

const CheckIcon: React.FC = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="size-4"
    aria-hidden="true"
  >
    <path
      d="M3.5 8.5L6.5 11.5L12.5 4.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ReminderEmailEditor: React.FC<ReminderEditorProps> = ({
  subjectFieldId,
  bodyFieldId,
  subject,
  body,
  templateId,
  isSubmitting,
  hasChanges,
  onSubjectChange,
  onBodyChange,
  onOpenEventInfo,
  onOpenSendNowDialog,
  onSendTest,
  onSave,
  subjectInputRef,
  bodyTextareaRef,
  showImageButton = true,
  showTimeUntilButton = true,
  showSendNowButton = true,
  disableSendNow = false,
  sendNowDisabledReason = null,
  isActionsDisabled = false,
  onInsertImagePlaceholder,
  onInsertTimeUntilEventPlaceholder,
  imagePlaceholderHtml,
  timeUntilEventPlaceholderHtml,
  subjectLimit = 120,
  bodyLimit = 2000,
}) => {
  const { t } = useTranslation();
  const editorRef = bodyTextareaRef;
  const bodyLabelId = useId();
  const toolbarId = useId();
  const [hasError, setHasError] = React.useState(false);

  // Track if we're currently editing to prevent cursor jumps
  const isUserEditingRef = React.useRef(false);
  const lastTemplateIdRef = React.useRef(templateId);

  /**
   * Sync editor HTML with external body state only when:
   * 1. Template changes (user switches between reminder templates or entry template)
   * 2. User is not actively editing (prevents cursor position loss)
   *
   * Note: body can change independently of templateId when:
   * - Image placeholder updates (primaryImageSrc loads)
   * - Defaults reset (resetFromDefaults called)
   * These changes should NOT sync to avoid disrupting user input.
   */
  React.useEffect(() => {
    const templateChanged = templateId !== lastTemplateIdRef.current;
    lastTemplateIdRef.current = templateId;
    if (editorRef.current && (templateChanged || !isUserEditingRef.current)) {
      if (editorRef.current.innerHTML !== body) {
        editorRef.current.innerHTML = body;
      }
    }
  }, [templateId, body, editorRef]);

  const handleEditorInput = () => {
    const html = editorRef?.current?.innerHTML ?? "";
    onBodyChange(html);
  };

  const handleEditorFocus = () => {
    isUserEditingRef.current = true;
  };

  const handleEditorBlur = () => {
    isUserEditingRef.current = false;
    handleEditorInput();
    // Validate on blur
    const plainText = editorRef?.current?.textContent?.trim() ?? "";
    setHasError(plainText.length === 0);
  };

  // Calculate character counts
  const subjectCharCount = subject.length;
  const bodyCharCount = editorRef?.current?.textContent?.trim().length ?? 0;
  const isSubjectOverLimit = subjectCharCount > subjectLimit;
  const isBodyOverLimit = bodyCharCount > bodyLimit;

  const insertImagePlaceholderAtCursor = () => {
    const editorEl = editorRef?.current;
    if (!editorEl) return;
    insertHtmlAtSelection(
      editorEl,
      imagePlaceholderHtml,
      onInsertImagePlaceholder,
      handleEditorInput,
    );
  };

  const insertTimeUntilEventPlaceholderAtCursor = () => {
    const editorEl = editorRef?.current;
    if (!editorEl) return;
    insertHtmlAtSelection(
      editorEl,
      timeUntilEventPlaceholderHtml || TIME_UNTIL_EVENT_TOKEN,
      onInsertTimeUntilEventPlaceholder,
      handleEditorInput,
    );
  };

  /**
   * CRITICAL SECURITY: Intercept paste events to prevent DOM-based XSS attacks.
   *
   * Without this handler, malicious HTML pasted into the contentEditable div
   * (e.g., <img src=x onerror=alert(1)>) would be rendered and executed immediately
   * by the browser before React can sanitize it.
   *
   * This handler:
   * 1. Prevents the default paste behavior
   * 2. Extracts HTML from the clipboard
   * 3. Sanitizes it using DOMPurify
   * 4. Manually inserts the sanitized content at the cursor position
   */
  const handleEditorPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();

    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    // Try to get HTML content first, fall back to plain text
    let content = clipboardData.getData("text/html");
    if (!content) {
      content = clipboardData.getData("text/plain");
    }

    if (!content) return;

    // CRITICAL: Always sanitize content before insertion to prevent XSS.
    const sanitized = sanitizeHtml(content);

    // Insert sanitized content at cursor position
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    // Create a temporary container to parse the sanitized HTML
    const temp = document.createElement("div");
    temp.innerHTML = sanitized;

    // Insert all nodes from the temporary container
    const fragment = document.createDocumentFragment();
    let lastNode: Node | null = null;
    while (temp.firstChild) {
      lastNode = fragment.appendChild(temp.firstChild);
    }
    range.insertNode(fragment);

    // Move cursor to end of inserted content
    if (lastNode) {
      range.setStartAfter(lastNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    // Trigger input event to update state
    handleEditorInput();
  };

  /**
   * CRITICAL SECURITY: Intercept drop events to prevent DOM-based XSS attacks.
   *
   * Similar to paste, drag-and-drop of HTML content can introduce malicious scripts.
   * This handler sanitizes dropped HTML before insertion.
   */
  const handleEditorDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const dataTransfer = e.dataTransfer;
    if (!dataTransfer) return;

    // Check if HTML content is being dropped
    const htmlContent = dataTransfer.getData("text/html");
    if (!htmlContent) return; // Let browser handle plain text drops

    e.preventDefault();

    // Sanitize the dropped HTML
    const sanitized = sanitizeHtml(htmlContent);

    // Insert sanitized content at drop position
    const selection = window.getSelection();
    if (!selection) return;

    // Create range at drop position
    const range = document.caretRangeFromPoint(e.clientX, e.clientY);
    if (!range) return;

    selection.removeAllRanges();
    selection.addRange(range);

    // Insert sanitized content
    const temp = document.createElement("div");
    temp.innerHTML = sanitized;

    const fragment = document.createDocumentFragment();
    let lastNode: Node | null = null;
    while (temp.firstChild) {
      lastNode = fragment.appendChild(temp.firstChild);
    }
    range.insertNode(fragment);

    // Move cursor to end of inserted content
    if (lastNode) {
      range.setStartAfter(lastNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    // Trigger input event to update state
    handleEditorInput();
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={subjectFieldId}>
            {t("eventManage.reminderEmailSubjectLabel")}
          </Label>
          <span
            className={`text-xs ${
              isSubjectOverLimit ? "font-medium text-red-500" : "text-gray-500"
            }`}
          >
            {subjectCharCount} / {subjectLimit}
          </span>
        </div>
        <Input
          id={subjectFieldId}
          ref={subjectInputRef}
          value={subject}
          onChange={(event) => onSubjectChange(event.target.value)}
          className={isSubjectOverLimit ? "border-red-500" : ""}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label id={bodyLabelId} htmlFor={bodyFieldId}>
            {t("eventManage.reminderEmailBodyLabel")}
          </Label>
          <span
            className={`text-xs ${isBodyOverLimit ? "font-medium text-red-500" : "text-gray-500"}`}
          >
            {bodyCharCount} / {bodyLimit}
          </span>
        </div>

        {/* Editor Toolbar */}
        <div
          id={toolbarId}
          role="toolbar"
          aria-label={t("eventManage.reminderEmailToolbar")}
          className="flex items-center gap-2 rounded-t-md border border-b-0 border-input bg-gray-50 px-3 py-2"
        >
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={onOpenEventInfo}
            className="h-8 text-xs"
            aria-label={t("eventManage.reminderEmailEventInfoButton")}
            title={t("eventManage.reminderEmailEventInfoButton")}
          >
            <Info className="mr-1.5 size-3.5" aria-hidden="true" />
            {t("eventManage.reminderEmailEventInfoButton")}
          </Button>
          {showTimeUntilButton && (
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={insertTimeUntilEventPlaceholderAtCursor}
              disabled={isActionsDisabled}
              className="h-8 text-xs"
              aria-label={t("eventManage.reminderEmailTimeUntilEventLabel")}
              title={t("eventManage.reminderEmailTimeUntilEventLabel")}
            >
              {t("eventManage.reminderEmailTimeUntilEventLabel")}
            </Button>
          )}
          {showImageButton && (
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={insertImagePlaceholderAtCursor}
              disabled={isActionsDisabled}
              className="h-8 text-xs"
              aria-label={t("eventManage.reminderEmailImagePlaceholderLabel")}
              title={t("eventManage.reminderEmailImagePlaceholderLabel")}
            >
              <ImagePlus className="mr-1.5 size-3.5" aria-hidden="true" />
              {t("eventManage.reminderEmailImagePlaceholderLabel")}
            </Button>
          )}
        </div>

        {/* biome-ignore lint/a11y/useSemanticElements: contentEditable div is required for HTML content editing, textarea only supports plain text */}
        <div
          id={bodyFieldId}
          ref={bodyTextareaRef}
          className="min-h-[220px] rounded-b-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
          role="textbox"
          contentEditable
          suppressContentEditableWarning
          aria-labelledby={bodyLabelId}
          aria-multiline="true"
          aria-required="true"
          aria-invalid={hasError}
          aria-describedby={toolbarId}
          tabIndex={0}
          onInput={handleEditorInput}
          onFocus={handleEditorFocus}
          onBlur={handleEditorBlur}
          onPaste={handleEditorPaste}
          onDrop={handleEditorDrop}
        />
        {hasError && (
          <p className="mt-1 text-sm text-red-500" role="alert">
            {t("eventManage.reminderEmailBodyRequired")}
          </p>
        )}
      </div>

      <div className="space-y-3">
        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="teal"
            onClick={onSave}
            disabled={isSubmitting || !hasChanges || isActionsDisabled}
            className="min-w-[120px]"
          >
            <CheckIcon />
            {t("common.save")}
          </Button>
          {showSendNowButton && (
            <Button
              variant="outline"
              onClick={onOpenSendNowDialog}
              disabled={isSubmitting || isActionsDisabled || disableSendNow}
            >
              <Send className="mr-2 size-4" />
              {t("eventManage.reminderEmailSendNowButton")}
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={onSendTest}
            disabled={isSubmitting || isActionsDisabled}
          >
            <Sparkles className="mr-2 size-4 text-rw-main" />
            {t("eventManage.reminderEmailSendTestButton")}
          </Button>
        </div>
        {disableSendNow && sendNowDisabledReason && (
          <p className="text-xs text-gray-500">{sendNowDisabledReason}</p>
        )}
      </div>
    </div>
  );
};
